import _ from 'lodash';
import Section from "../components/Section";
import {Button} from "../components/Button";
import {
    CLAIMING_DISABLED, CollectibleNamesByAddress,
    ContractAddresses,
    ModalTypes,
    STAKING_DISABLED, SubchainGovernanceTokenSymbol
} from "../constants";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {formatBalance, truncate} from "../utils";
import Wallet from "../store/models/wallet";
import React, {useEffect, useState} from "react";
import {usePageVisibility} from "../hooks/usePageVisibility";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import logo from "../assets/logos/grove@2x.png";
import thetaWalletLogo from "../assets/wallets/theta-wallet-white@2x.png";
import metamaskLogo from "../assets/wallets/metamask-white@2x.png";

export const selectCurrentAccount = state => state?.models?.currentAccount;
export const selectCurrentBalances = state => state?.models?.balances;
export const selectNftTokenIds = state => state?.models?.nftTokenIdsByCollectibleAddress;

const ClaimPage = () => {
    const {t} = useTranslation();
    const isVisible = usePageVisibility();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const account = useSelector(selectCurrentAccount);
    const balances = useSelector(selectCurrentBalances);
    let nftTokenIdsByCollectibleAddress = useSelector(selectNftTokenIds);
    let nftTokenIds = _.flatten(_.values(nftTokenIdsByCollectibleAddress));
    let groveWarsCommemorativeNFTTokenIds = nftTokenIdsByCollectibleAddress[ContractAddresses.GroveWarsCommemorativeNFT];
    let pineOGCommemorativeNFTTokenIds = nftTokenIdsByCollectibleAddress[ContractAddresses.PineOGCommemorativeNFT];
    const [amountGWC, setAmountGWC] = useState('');
    const [amountPOGC, setAmountPOGC] = useState('');
    const onClaim = (collectibleAddress) => {
        // Fetch token ids
        let amount = collectibleAddress === ContractAddresses.GroveWarsCommemorativeNFT ? amountGWC : amountPOGC;
        if(parseInt(amount) > 0){
            let tokenIds = _.take(nftTokenIdsByCollectibleAddress[collectibleAddress], parseInt(amount));
            dispatch(Wallet.actions.claim(collectibleAddress, tokenIds));
        }
        else{
            alert('Please enter a valid amount to burn');
        }

    }

    const onMetaMask = () => {
        dispatch(Wallet.actions.loginWithMetamask());
    }

    const onThetaWallet = () => {
        dispatch(Wallet.actions.loginWithWalletConnect());
    }

    const onDisconnect = () => {
        dispatch(Wallet.actions.disconnect());
    }

    const onStake = () => {
        navigate('/stake');
    }

    const onAll = (collectibleAddress) => {
        let tokenIds = nftTokenIdsByCollectibleAddress[collectibleAddress];
        if(collectibleAddress === ContractAddresses.GroveWarsCommemorativeNFT){
            setAmountGWC(tokenIds?.length);
        }
        else if(collectibleAddress === ContractAddresses.PineOGCommemorativeNFT){
            setAmountPOGC(tokenIds?.length);
        }
    }

    useEffect(() => {
        (async () => {
            await dispatch(Wallet.actions.tryToRecoverWalletSession());
            dispatch(Wallet.actions.getNftTokenIds(ContractAddresses.GroveWarsCommemorativeNFT));
            dispatch(Wallet.actions.getNftTokenIds(ContractAddresses.PineOGCommemorativeNFT));
        })();
    }, []);

    useEffect(() => {
        if (isVisible && account && !_.isEmpty(balances)) {
            dispatch(Wallet.actions.getBalances());
            dispatch(Wallet.actions.getNftTokenIds(ContractAddresses.GroveWarsCommemorativeNFT));
            dispatch(Wallet.actions.getNftTokenIds(ContractAddresses.PineOGCommemorativeNFT));
        }
    }, [isVisible, account]);

    return (
        <div className={'Page ClaimPage'}>
            <Section className={'TopSection'}>
                {
                    _.isNil(account) &&
                    <div className={'SectionContainer'}>
                        <img src={logo}
                             className={'StakePage__logo'}
                        />
                        <h1>
                            {t('claim.title')}
                        </h1>
                        <p>
                            {t('claim.description')}
                        </p>

                        <div className={'ConnectYourWallet'}>
                            <div className={'ConnectYourWallet__title'}>
                                {t('stake.connect_your_wallet')}
                            </div>
                            <div className={'ConnectYourWallet__options'}>
                                <div className={'ConnectYourWallet__option'}>
                                    <Button size={'large'}
                                            title={'Theta Wallet'}
                                            className={'ConnectWalletButton'}
                                            onClick={onThetaWallet}
                                    >
                                        <img src={thetaWalletLogo}/>
                                    </Button>
                                    <div className={'ConnectWalletButtonAttachment'}>
                                        {t('stake.available_on_ios_and_android')}
                                    </div>
                                </div>
                                <div className={'ConnectYourWallet__option'}>
                                    <Button size={'large'}
                                            title={'Metamask'}
                                            className={'ConnectWalletButton'}
                                            onClick={onMetaMask}
                                    >
                                        <img src={metamaskLogo}/>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                }
                {
                    !_.isNil(account) &&
                    <div className={'SectionContainer'}>
                        {
                            CLAIMING_DISABLED &&
                            <div className={'WarningMessage'}>
                                {t('claim.paused_message')}
                            </div>
                        }


                        <p className={'ClaimPage__connection'}>
                            <span className={'ClaimPage__connection-title'}>Connected to: </span>
                            <span className={'ClaimPage__connection-account'}>{truncate(account)}</span>
                            <a className={'ClaimPage__connection-disconnect'}
                               onClick={onDisconnect}>
                                {t('claim.disconnect')}
                            </a>
                        </p>

                        <h1>
                            {t('claim.burn_title')}
                        </h1>

                        <div className={'AccountBalances'}>
                            <div className={'AccountBalanceRow'}>
                                <div className={'AccountBalanceRow__title'}>
                                    {t('claim.balance')}
                                </div>
                                <div className={'AccountBalanceRow__value'}>
                                    {formatBalance(balances?.governanceTokenOnHand)}
                                </div>
                            </div>
                        </div>

                        {
                            balances?.governanceTokenOnHand > 0 &&
                            <Button color={'brand'}
                                    size={'medium'}
                                    className={'ClaimPage__stake-button'}
                                    onClick={onStake}
                            >
                                {t('claim.stake')}
                            </Button>
                        }


                        <div className={'ClaimPage__claimable-cards'}>


                        </div>

                        {
                            nftTokenIds?.length > 0 &&
                            <div className={'BurnAndRedeems'}>
                                <div className={'BurnAndRedeem'}>
                                    <div className={'BurnAndRedeem__name'}>
                                        {CollectibleNamesByAddress[ContractAddresses.GroveWarsCommemorativeNFT]}
                                    </div>
                                    <img className={'BurnAndRedeem__img'}
                                         src={'https://user-assets-thetadrop.imgix.net/user_rzgh9jwdchn0qatzfqkef0218xf/coll_mc2vwstzd9hh709nzu7ff8jv9tb/type/image-1712356275941.png?w=400'}/>
                                    <div className={'BurnAndRedeem__owned'}>
                                        <div className={'BurnAndRedeem__owned-title'}>
                                            {t('claim.owned')}
                                        </div>
                                        <div className={'BurnAndRedeem__owned-amount'}>
                                            {groveWarsCommemorativeNFTTokenIds?.length}
                                        </div>
                                    </div>
                                    <div className={'ActionInputWrapper'}>
                                        <div className={'Input'}>
                                            <input value={amountGWC}
                                                   onChange={(e) => setAmountGWC(e.target.value)}
                                                   placeholder={t('claim.enter_amount')}
                                                   type="number"
                                                   max={groveWarsCommemorativeNFTTokenIds?.length}
                                                   min={0}
                                            />
                                            <Button color={'grey'}
                                                    size={'xsmall'}
                                                    onClick={() => onAll(ContractAddresses.GroveWarsCommemorativeNFT)}
                                            >
                                                {t('claim.all')}
                                            </Button>
                                        </div>

                                        <Button color={'brand'}
                                                size={'medium'}
                                                onClick={() => onClaim(ContractAddresses.GroveWarsCommemorativeNFT)}
                                        >
                                            {t('claim.burn')}
                                        </Button>
                                    </div>
                                    <div className={'BurnAndRedeem__reward'}>
                                        {
                                            `Earn 500 $${SubchainGovernanceTokenSymbol} per NFT burned`
                                        }
                                    </div>
                                </div>
                                <div className={'BurnAndRedeem'}>
                                    <div className={'BurnAndRedeem__name'}>
                                        {CollectibleNamesByAddress[ContractAddresses.PineOGCommemorativeNFT]}
                                    </div>
                                    <img className={'BurnAndRedeem__img'}
                                         src={'https://user-assets-thetadrop.imgix.net/user_rzgh9jwdchn0qatzfqkef0218xf/coll_4py146t24dnryg5p6vpw5pnrrsa/type/image-1714781240527.png?w=400'}/>
                                    <div className={'BurnAndRedeem__owned'}>
                                        <div className={'BurnAndRedeem__owned-title'}>
                                        {t('claim.owned')}
                                        </div>
                                        <div className={'BurnAndRedeem__owned-amount'}>
                                            {pineOGCommemorativeNFTTokenIds?.length}
                                        </div>
                                    </div>
                                    <div className={'ActionInputWrapper'}>
                                        <div className={'Input'}>
                                            <input value={amountPOGC}
                                                   onChange={(e) => setAmountPOGC(e.target.value)}
                                                   placeholder={t('claim.enter_amount')}
                                                   type="number"
                                                   max={pineOGCommemorativeNFTTokenIds?.length}
                                                   min={0}
                                            />
                                            <Button color={'grey'}
                                                    size={'xsmall'}
                                                    onClick={() => onAll(ContractAddresses.PineOGCommemorativeNFT)}
                                            >
                                                {t('claim.all')}
                                            </Button>
                                        </div>

                                        <Button color={'brand'}
                                                size={'medium'}
                                                onClick={() => onClaim(ContractAddresses.PineOGCommemorativeNFT)}
                                        >
                                            {t('claim.burn')}
                                        </Button>
                                    </div>
                                    <div className={'BurnAndRedeem__reward'}>
                                        {
                                            `Earn 1,000 $${SubchainGovernanceTokenSymbol} per NFT burned`
                                        }
                                    </div>
                                </div>
                            </div>
                        }

                        {
                            nftTokenIds?.length === 0 &&
                            <div className={'ClaimPage__no-nfts'}>
                                <h3>{t('claim.no_nfts.title')}</h3>
                                <p>{t('claim.no_nfts.subtitle')}</p>
                                <Button className={'ClaimPage__burn-all-button'}
                                        color={'transparent-green'}
                                        size={'medium'}
                                        href={'https://sps.thetadrop.com/'}
                                >
                                    {t('claim.no_nfts.button')}
                                </Button>
                            </div>
                        }

                    </div>
                }
            </Section>
        </div>
    )
};

export default ClaimPage;